import React, { useCallback, useEffect, useState } from "react";
import { offersAPI, productsAPI, variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDate, isoDay, todayIso as today } from "../utils/dates";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Promotions that actually come off a price.
 *
 * Until an offer is pointed at something it does nothing, so the products it
 * covers are part of the same form rather than a separate step — an offer
 * saved with nothing attached would look finished and change no prices.
 */
const TYPES = ["percentage", "fixed", "buy_x_get_y"];

const inAMonth = () => isoDay(new Date(Date.now() + 30 * 86400000));

const blank = () => ({
  title: "",
  description: "",
  offer_type: "percentage",
  discount_value: "10",
  buy_quantity: "2",
  get_quantity: "1",
  applies_to_all: false,
  start_date: today(),
  end_date: inAMonth(),
  is_active: true,
  product_ids: [],
  group_ids: [],
});

const Offers = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, p, r] = await Promise.all([
        offersAPI.getAll(),
        productsAPI.getAll(),
        // A member who may edit offers but not browse ranges simply sees none
        variantGroupsAPI.list().catch(() => ({ data: [] })),
      ]);
      setOffers(o.data);
      setProducts(p.data);
      setRanges(r.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("offers.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    const payload = {
      title: editing.title,
      description: editing.description || null,
      offer_type: editing.offer_type,
      discount_value:
        editing.offer_type === "buy_x_get_y"
          ? 0
          : parseFloat(editing.discount_value),
      buy_quantity:
        editing.offer_type === "buy_x_get_y"
          ? parseInt(editing.buy_quantity, 10)
          : null,
      get_quantity:
        editing.offer_type === "buy_x_get_y"
          ? parseInt(editing.get_quantity, 10)
          : null,
      applies_to_all: editing.applies_to_all,
      start_date: editing.start_date,
      end_date: editing.end_date,
      is_active: editing.is_active,
    };

    try {
      const saved = editing.id
        ? await offersAPI.update(editing.id, payload)
        : await offersAPI.create(payload);

      const id = editing.id || saved.data.offer.id;

      // Always sent, including empty: clearing the list is how an owner turns
      // a product offer into a shop-wide one
      await offersAPI.setProducts(
        id,
        editing.applies_to_all ? [] : editing.product_ids,
        editing.applies_to_all ? [] : editing.group_ids,
      );

      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("offers.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (offer) => {
    try {
      await offersAPI.toggleStatus(offer.id, !offer.is_active);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("offers.saveFailed"));
    }
  };

  // Products and ranges are searched together: "Mesh" finds the range and its
  // variants, and a range is the one to pick when every variant should be covered
  const matches = search.trim()
    ? [
        ...ranges
          .filter(
            (range) =>
              !editing?.group_ids?.includes(range.id) &&
              range.name.toLowerCase().includes(search.trim().toLowerCase()),
          )
          .map((range) => ({ kind: "range", id: range.id, name: range.name })),
        ...products
          .filter(
            (product) =>
              !editing?.product_ids.includes(product.id) &&
              product.name.toLowerCase().includes(search.trim().toLowerCase()),
          )
          .map((product) => ({ kind: "product", id: product.id, name: product.name })),
      ].slice(0, 8)
    : [];

  const nameOf = (id) => products.find((p) => p.id === id)?.name || id;
  const rangeNameOf = (id) => ranges.find((r) => r.id === id)?.name || id;

  const live = (offer) =>
    offer.is_active &&
    String(offer.start_date).slice(0, 10) <= today() &&
    String(offer.end_date).slice(0, 10) >= today();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("offers.title")}
          </h1>
          <p className="text-gray-600">{t("offers.subtitle")}</p>
        </div>
        {can("offers.edit") && (
          <button
            onClick={() => setEditing(editing ? null : blank())}
            className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
          >
            {editing ? t("common.cancel") : t("offers.new")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {editing && (
        <form
          onSubmit={save}
          className="bg-white rounded-xl shadow-lg p-6 space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("offers.titleLabel")}
              </span>
              <input
                type="text"
                required
                placeholder={t("offers.titlePlaceholder")}
                value={editing.title}
                onChange={(e) =>
                  setEditing({ ...editing, title: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("offers.kind")}
              </span>
              <select
                value={editing.offer_type}
                onChange={(e) =>
                  setEditing({ ...editing, offer_type: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                {TYPES.map((value) => (
                  <option key={value} value={value}>
                    {t(`offers.types.${value}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {editing.offer_type === "buy_x_get_y" ? (
            <div className="grid gap-4 sm:grid-cols-2 max-w-md">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {t("offers.buy")}
                </span>
                <input
                  type="number"
                  min="1"
                  value={editing.buy_quantity}
                  onChange={(e) =>
                    setEditing({ ...editing, buy_quantity: e.target.value })
                  }
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {t("offers.getFree")}
                </span>
                <input
                  type="number"
                  min="1"
                  value={editing.get_quantity}
                  onChange={(e) =>
                    setEditing({ ...editing, get_quantity: e.target.value })
                  }
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </label>
              <p className="sm:col-span-2 text-xs text-gray-500">
                {t("offers.needsAll", {
                  count:
                    Number(editing.buy_quantity || 0) +
                    Number(editing.get_quantity || 0),
                })}
              </p>
            </div>
          ) : (
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-gray-700">
                {editing.offer_type === "percentage"
                  ? t("offers.percentOff")
                  : t("offers.amountOff", { currency })}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={editing.discount_value}
                onChange={(e) =>
                  setEditing({ ...editing, discount_value: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {editing.offer_type === "fixed" && (
                <span className="text-xs text-gray-500">
                  {t("offers.fixedHint")}
                </span>
              )}
            </label>
          )}

          <div className="grid gap-4 sm:grid-cols-2 max-w-md">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("common.from")}
              </span>
              <input
                type="date"
                required
                value={editing.start_date?.slice(0, 10)}
                onChange={(e) =>
                  setEditing({ ...editing, start_date: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("common.until")}
              </span>
              <input
                type="date"
                required
                value={editing.end_date?.slice(0, 10)}
                onChange={(e) =>
                  setEditing({ ...editing, end_date: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editing.applies_to_all}
                onChange={(e) =>
                  setEditing({ ...editing, applies_to_all: e.target.checked })
                }
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-gray-700">
                {t("offers.everything")}
              </span>
            </label>

            {!editing.applies_to_all && (
              <div className="mt-3">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("offers.searchProduct")}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                {matches.length > 0 && (
                  <ul className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {matches.map((match) => (
                      <li key={`${match.kind}:${match.id}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(
                              match.kind === "range"
                                ? {
                                    ...editing,
                                    group_ids: [...editing.group_ids, match.id],
                                  }
                                : {
                                    ...editing,
                                    product_ids: [...editing.product_ids, match.id],
                                  },
                            );
                            setSearch("");
                          }}
                          className="w-full text-start px-4 py-2 hover:bg-gray-50"
                        >
                          {match.kind === "range" && (
                            <span className="me-2 text-xs px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">
                              🗂️ {t("offers.rangeTag")}
                            </span>
                          )}
                          {match.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {editing.group_ids.map((id) => (
                    <span
                      key={`range:${id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-primary-50 text-primary-800 px-3 py-1 text-sm"
                    >
                      🗂️ {rangeNameOf(id)}
                      <button
                        type="button"
                        aria-label={t("offers.removeProduct", {
                          name: rangeNameOf(id),
                        })}
                        onClick={() =>
                          setEditing({
                            ...editing,
                            group_ids: editing.group_ids.filter(
                              (value) => value !== id,
                            ),
                          })
                        }
                        className="text-primary-400 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  {editing.product_ids.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm"
                    >
                      {nameOf(id)}
                      <button
                        type="button"
                        aria-label={t("offers.removeProduct", {
                          name: nameOf(id),
                        })}
                        onClick={() =>
                          setEditing({
                            ...editing,
                            product_ids: editing.product_ids.filter(
                              (value) => value !== id,
                            ),
                          })
                        }
                        className="text-gray-400 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>

                {editing.product_ids.length === 0 &&
                  editing.group_ids.length === 0 && (
                    <p className="mt-2 text-sm text-amber-700">
                      {t("offers.coversNothingHint")}
                    </p>
                  )}
                {editing.group_ids.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">
                    {t("offers.rangeHint")}
                  </p>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
          >
            {busy ? t("common.saving") : t("offers.save")}
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {offers.length === 0 ? (
          <EmptyState screen="offers" message={t("offers.none")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {offers.map((offer) => (
              <li key={offer.id} className="py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 flex flex-wrap items-center gap-2">
                      {offer.title}
                      {live(offer) ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 whitespace-nowrap">
                          {t("offers.live")}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                          {t("offers.notRunning")}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t(`offers.types.${offer.offer_type}`)}
                      {offer.offer_type === "percentage" &&
                        ` · ${Number(offer.discount_value)}%`}
                      {offer.offer_type === "fixed" &&
                        ` · ${formatMoney(offer.discount_value, currency)}`}
                      {offer.offer_type === "buy_x_get_y" &&
                        ` · ${t("offers.summaryBuyGet", {
                          buy: offer.buy_quantity,
                          get: offer.get_quantity,
                        })}`}
                      {" · "}
                      {formatDate(offer.start_date)} – {formatDate(offer.end_date)}
                    </p>
                    <p className="text-sm mt-0.5">
                      {offer.applies_to_all ? (
                        <span className="text-gray-600">
                          {t("offers.everything")}
                        </span>
                      ) : offer.product_ids?.length || offer.group_ids?.length ? (
                        <span className="text-gray-600">
                          {[
                            offer.product_ids?.length
                              ? t("common.products", {
                                  count: offer.product_ids.length,
                                })
                              : null,
                            offer.group_ids?.length
                              ? t("offers.rangesCount", {
                                  count: offer.group_ids.length,
                                })
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      ) : (
                        <span className="text-amber-700">
                          {t("offers.coversNothing")}
                        </span>
                      )}
                    </p>
                    {/* Allowed — a loss leader can be deliberate — never unnoticed */}
                    {offer.below_cost_products?.length > 0 && (
                      <p className="mt-1 text-sm font-medium text-amber-700">
                        ⚠️{" "}
                        {t("offers.belowCost", {
                          names: offer.below_cost_products
                            .map((product) => product.name)
                            .join(", "),
                        })}
                      </p>
                    )}
                  </div>

                  {can("offers.edit") && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggle(offer)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                      >
                        {offer.is_active ? t("offers.pause") : t("offers.resume")}
                      </button>
                      <button
                        onClick={() =>
                          setEditing({
                            ...offer,
                            product_ids: offer.product_ids || [],
                            group_ids: offer.group_ids || [],
                            discount_value: String(offer.discount_value ?? ""),
                            buy_quantity: String(offer.buy_quantity ?? 2),
                            get_quantity: String(offer.get_quantity ?? 1),
                          })
                        }
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                      >
                        {t("common.edit")}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Offers;
